<template>
    <div class="background">
        <div id="sca-payment-request-button" class="payment-request">
        </div>
        <PaymentError :error="displayError"/>
        <FacebookBrowserMessage v-if="showFacebookMessage" :subdomain="subdomain"/>
    </div>
</template>

<script>
import PaymentError from '../PaymentError.vue';
import {
  mountPaymentRequestsButtonPlatform,
  createPaymentRequestPlatform,
  instantiateStripePlatform,
  subscribe,
  confirmPaymentRequestSecret,
  confirmSetupPaymentRequestSecret,
} from '../stripe-elements';
import PaymentsMixin from '../PaymentsMixin.vue';
import FacebookBrowserMessage from '../FacebookBrowserMessage.vue';

export default {
  name: 'SCACreateSubscriptionPaymentRequestButton',
  mixins: [PaymentsMixin],
  data() {
    return {
      canUsePaymentRequest: false,
      displayError: '',
      stripe: {},
      elements: {},
    };
  },
  props: {
    stripePublicKey: {
      type: String,
      required: true,
    },
    stripeAccountId: {
      type: String,
      required: true,
    },
    orderId: {
      type: String,
      required: false,
    },
    paymentRequestOptions: {
      required: true,
      type: Object,
    },
    subdomain: {
      required: true,
      type: String,
    },
  },
  methods: {
    async submitPaymentRequest(event) {
      this.$emit('emit-global-loading', true);
      this.displayError = '';
      this.subscribe(event);
    },

    async subscribe(event) {
      const { data, status } = await subscribe(event.paymentMethod.id, this.orderId);
      if (status === false) {
        // fail
        this.handleErrorMessages(data);
        event.complete('fail');
      } else if (data.status === 'trialing' && !data.pending_setup_intent) {
        // non sca
        const { latest_invoice } = data;
        const paymentIntentId = latest_invoice.payment_intent ? latest_invoice.payment_intent.id : undefined;
        event.complete('success');
        this.confirmTheSubscriptionAndRedirect(data.id, paymentIntentId);
      } else {
        // sca
        event.complete('success');
        this.strongCustomerAuthentication(event, data);
      }
    },

    async strongCustomerAuthentication(event, data) {
      const subscription = data;
      const latestInvoice = subscription.latest_invoice;
      const paymentIntent = latestInvoice.payment_intent;
      const setupIntent = subscription.pending_setup_intent;
      let confirmation = {};
      confirmation = await this.sendSecret(paymentIntent, setupIntent, event);
      if (confirmation.error) {
        this.handleErrorMessages(confirmation);
      } else {
        const { subscriptionSetupSuccessful, paymentIntentId } = this.getSuccessfulSubscriptionStatus(latestInvoice, confirmation);
        if (subscriptionSetupSuccessful === true) {
          this.confirmTheSubscriptionAndRedirect(subscription.id, paymentIntentId);
        } else {
          this.handleErrorMessages();
        }
      }
    },

    async sendSecret(paymentIntent, setupIntent, event) {
      let confirmation;
      if (paymentIntent && paymentIntent.status === 'requires_action') {
        confirmation = await confirmPaymentRequestSecret(paymentIntent.client_secret, event.paymentMethod.id);
      }
      if (setupIntent && setupIntent.status === 'requires_action') {
        confirmation = await confirmSetupPaymentRequestSecret(setupIntent.client_secret, event.paymentMethod.id);
      }
      return confirmation;
    },
  },

  beforeMount() {
    const { stripePublicKey } = this;
    const { stripe, elements } = instantiateStripePlatform(Stripe, {
      stripePublicKey,
    });
    this.stripe = stripe;
    this.elements = elements;
  },

  async mounted() {
    const paymentRequest = createPaymentRequestPlatform(this.paymentRequestOptions, this.stripe );
    const prButton = mountPaymentRequestsButtonPlatform(paymentRequest, this.elements);
    const nodeId = '#sca-payment-request-button';
    paymentRequest.canMakePayment()
      .then((result) => {
        const button = document.querySelector(nodeId);
        if (result) {
          this.$emit('valid', true);
          if (result.applePay === false) {
            button.classList.add('google-pay');
            button.disabled = true;
            button.addEventListener('click', () => {
              paymentRequest.show();
              this.displayError = '';
            });
          } else {
            prButton.mount(nodeId);
          }
        } else {
          this.paymentRequestFailedToMount = true;
          button.style.display = 'none';
        }
      });
    paymentRequest.on('paymentmethod', this.submitPaymentRequest);
  },
  components: {
    PaymentError,
    FacebookBrowserMessage,
  },
};
</script>

<style scoped>

    .background {
        padding: 0 16px;
    }

    #sca-payment-request-button {
        padding-bottom: 16px;
    }

    /*
      Some of the seemingly arbitrary figures come from this doc
      https://developers.google.com/pay/api/android/guides/brand-guidelines#:~:text=The%20Google%20Pay%20button%20should,minimum%20width%20of%20152%20dp.
    */
    #sca-payment-request-button.google-pay {
        background-color: #444;
        background: url(/assets-prod/images/svg-sprites/Google-Pay-Logo.svg) no-repeat center #fff;
        margin: 0 auto;
        height: 40px;
        background-size: 48px;
        min-width: 160px;
        /*max-width: 24vw;*/
        cursor: pointer;
        text-align: center;
        padding: 8px;
        border: 1px solid #fafafa;
        border-radius: 4px;
        will-change: box-shadow;
        box-shadow: 0px 3px 1px -2px rgba(0, 0, 0, 0.2), 0px 2px 2px 0px rgba(0, 0, 0, 0.14), 0px 1px 5px 0px rgba(0, 0, 0, 0.12);

    }

    .google-pay:after {
        content: '\0020'
    }

</style>
